import { type Basket, type OrderPaymentInstrument, PaymentMethodId } from '../baskets/model';
import type {
    CustomerPaymentInstrument,
    CustomerPaymentInstrumentRequest,
} from '../customers/model';
import type { CustomerPaymentCard } from '../customers/model/CustomerPaymentCard';

export const getPaymentByMethodId = (
    basket: Basket | undefined,
    methodId: PaymentMethodId,
): OrderPaymentInstrument | undefined =>
    basket?.paymentInstruments?.find(({ paymentMethodId }) => paymentMethodId === methodId);

export const hasPaymentByMethodId = (
    basket: Basket | undefined,
    methodId: PaymentMethodId,
): boolean =>
    !!basket?.paymentInstruments?.some(({ paymentMethodId }) => paymentMethodId === methodId);

export const getPaymentsByMethodId = (
    basket: Basket | undefined,
    paymentMethodId: PaymentMethodId,
): OrderPaymentInstrument[] =>
    basket?.paymentInstruments?.filter((payment) => payment.paymentMethodId === paymentMethodId) ??
    [];

export const getPaymentsExceptMethodIds = (
    basket: Basket | undefined,
    paymentMethodIds: PaymentMethodId[],
): OrderPaymentInstrument[] =>
    basket?.paymentInstruments?.filter(
        (payment) => !paymentMethodIds.includes(payment.paymentMethodId as PaymentMethodId),
    ) ?? [];

export const getCreditCardPayment = (
    basket: Basket | undefined,
): OrderPaymentInstrument | undefined => getPaymentByMethodId(basket, PaymentMethodId.CREDIT_CARD);

export const hasGiftCardPayment = (basket: Basket | undefined): boolean =>
    !!getPaymentByMethodId(basket, PaymentMethodId.GIFT_CARD);

export const toCustomerPaymentInstrument = ({
    paymentMethodId,
    paymentInstrumentId,
    paymentCard,
}: OrderPaymentInstrument): CustomerPaymentInstrument => ({
    paymentInstrumentId,
    paymentMethodId: paymentMethodId!,
    paymentCard: paymentCard!,
});

export const buildOrderPaymentInstrument = ({
    paymentInstrumentId,
    paymentMethodId,
    paymentCard: {
        cardType,
        expirationMonth,
        expirationYear,
        holder,
        maskedNumber,
        numberLastDigits,
        c_creditCardNumber,
        c_securityCode,
    },
}: CustomerPaymentInstrument): OrderPaymentInstrument => ({
    paymentMethodId: paymentMethodId ?? PaymentMethodId.CREDIT_CARD,
    paymentInstrumentId,
    customerPaymentInstrumentId: paymentInstrumentId,
    paymentCard: {
        cardType,
        expirationMonth,
        expirationYear,
        holder,
        maskedNumber,
        numberLastDigits,
    },
    c_creditCardNumber,
    c_securityCode,
});

export const buildCustomerPaymentInstrumentRequest = ({
    cardType,
    expirationMonth,
    expirationYear,
    holder,
    c_creditCardNumber,
}: CustomerPaymentCard): CustomerPaymentInstrumentRequest => ({
    paymentCard: {
        cardType: cardType!,
        expirationMonth: expirationMonth!,
        expirationYear: expirationYear!,
        holder: holder!,
        number: c_creditCardNumber!,
    },
    paymentMethodId: PaymentMethodId.CREDIT_CARD,
});
